import { SxProps } from '@mui/material';
import { t } from 'i18next';
import { upperFirst } from 'lodash';

import { FormField } from 'core/components';
import { AllowedDecimalsTooltip, FlexBox, FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { PricingHelper } from 'pricing/helpers';
import { CostLockTooltip } from 'quotes/elements';
import { ICalculatePnbPriceForm, ICustomerQuotePart } from 'quotes/types';

export const getDefaultValues = (selectedQuotePart: ICustomerQuotePart): ICalculatePnbPriceForm => {
  if (!selectedQuotePart) {
    return { allSameBase: false };
  }

  return {
    computedPurchaseCost: selectedQuotePart.computedPurchaseCost,
    computedCost: selectedQuotePart.computedCost,
    price: selectedQuotePart.price,
    margin: formatMargin(selectedQuotePart.computedCost, selectedQuotePart.price),

    computedPurchaseCostBase: selectedQuotePart.computedPurchaseCostBase,
    computedCostBase: selectedQuotePart.computedCostBase,
    priceBase: selectedQuotePart.priceBase,
    marginBase: formatMargin(selectedQuotePart.computedCostBase, selectedQuotePart.priceBase),

    computedPurchaseCostAdder: selectedQuotePart.computedPurchaseCostAdder,
    computedCostAdder: selectedQuotePart.computedCostAdder,
    priceAdder: selectedQuotePart.priceAdder,
    marginAdder: formatMargin(selectedQuotePart.computedCostAdder, selectedQuotePart.priceAdder),
    allSameBase: false,
  };
};

export const getLabelByType = (label: string) => {
  let labelType = '';

  switch (label) {
    case PricingHelper.PRICING_TYPE_BASE:
      labelType = 'base';
      break;
    case PricingHelper.PRICING_TYPE_ADDER:
      labelType = 'adder';
      break;
    case PricingHelper.PRICING_TYPE_TOTAL:
    default:
      labelType = 'unit';
      break;
  }

  return upperFirst(labelType);
};

export const formatCost = (purchaseCost: string | number, multiplier = 0, decimals: number) => {
  const cost = PricingHelper.computePrice(Number(purchaseCost), multiplier);
  if (!cost) {
    return null;
  }

  return NumberHelper.round(cost, decimals);
};

export const formatMargin = (cost: number, price: number) => {
  const margin = PricingHelper.computeGpm(cost, price);

  if (!margin) {
    return null;
  }

  return NumberHelper.round(margin * 100);
};

export const formatPrice = (cost: number, margin: number) => {
  const price = PricingHelper.computePrice(cost, margin / 100, false);
  if (!price) {
    return null;
  }

  return NumberHelper.round(price);
};

export const getFieldByType = (field: 'price' | 'margin' | 'computedCost', type: string) => {
  if (PricingHelper.PRICING_TYPE_TOTAL !== type) {
    field += upperFirst(type);
  }

  return field;
};

export const calculatePricingByBaseAndAdder = (
  pricingBase: number,
  pricingAdder: number,
  adderInfo: number,
  quotePriceDecimals: number
) => NumberHelper.round(Number(pricingBase) + Number(pricingAdder) * adderInfo, quotePriceDecimals);

export const getFormGroupFieldPricing = ({
  name,
  quotePart,
  type,
  onPurchaseCostChange,
  onMarginChange,
  disabled = false,
  containerStyle = {},
  isPriceLocked = false,
  isMarginDisabled = false,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
}: {
  name: string;
  quotePart: ICustomerQuotePart;
  type: string;
  onPurchaseCostChange?: (event?: { target: { value: string }; source: 'prop' | 'event' }) => void;
  onMarginChange?: (event?: { target: { value: string }; source: 'prop' | 'event' }) => void;
  disabled?: boolean;
  containerStyle?: SxProps;
  isPriceLocked?: boolean;
  isMarginDisabled?: boolean;
  costDecimals?: number;
}) => {
  const fieldNameType = type === PricingHelper.PRICING_TYPE_TOTAL ? '' : upperFirst(type);
  const labelType = getLabelByType(type);

  return (
    <FormGroupWrapper name={name} sx={containerStyle}>
      <FormField
        name={`computedPurchaseCost${fieldNameType}`}
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: (
            <FlexBox>
              {t(`label${labelType}PurchaseCost`)}
              {PricingHelper.PRICING_TYPE_TOTAL === type && (
                <CostLockTooltip quotePart={quotePart} />
              )}
            </FlexBox>
          ),
          placeholder: t(`label${labelType}PurchaseCost`),
          disabled,
          onChange: onPurchaseCostChange ? onPurchaseCostChange : undefined,
          endDecorator: <AllowedDecimalsTooltip decimals={costDecimals} />,
          slotProps: {
            input: {
              thousandSeparator: true,
              decimalScale: costDecimals,
              fixedDecimalScale: true,
              prefix: '$',
            },
          },
        }}
      />
      <FormField
        name={`margin${fieldNameType}`}
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t(`label${labelType}Margin`),
          placeholder: t(`label${labelType}Margin`),
          disabled: disabled || isPriceLocked || isMarginDisabled,
          onChange: onMarginChange ? onMarginChange : undefined,
          slotProps: {
            input: {
              max: 100,
              decimalScale: 2,
              allowNegative: true,
              suffix: '%',
            },
          },
        }}
      />
    </FormGroupWrapper>
  );
};
